import GoogleMapApi from '@/libs/map/gmap'
import { selectDetailMenuSite } from '@/redux/meta/selector'
import initScript from '@/utils/initScript'
import { getLanguageCode, parseLocale } from '@/utils/localisation'
import { useParams } from 'next/navigation'
import React, { ReactNode, useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

interface ReverseGeocodeServiceProps {
  children: (props: {
    reverseGeocode: (
      lat: number,
      lng: number
    ) => Promise<MapAddress.TransformedAddress[] | null>
    isLoading: boolean
  }) => ReactNode
}

const ReverseGeocodeService: React.FC<ReverseGeocodeServiceProps> = ({
  children,
}) => {
  const params = useParams()
  const { language, country } = parseLocale(params?.locale as string)
  const { googleMapId: apiKey } = useSelector(selectDetailMenuSite) || {}
  const [isLoading, setIsLoading] = useState(false)
  const [mapApi, setMapApi] = useState<ReturnType<typeof GoogleMapApi> | null>(
    null
  )

  const initializeMapApi = useCallback(async () => {
    if (mapApi) return

    setIsLoading(true)
    try {
      const { language } = getLocalisationParams()
      await initScript(
        `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=${language}&loading=async`,
        `google-maps-js-${language}`
      )

      // Wait for the Google Maps API to be fully initialized
      await new Promise<void>((resolve) => {
        if (window.google?.maps?.places) {
          resolve()
        } else {
          const checkInterval = setInterval(() => {
            if (window.google?.maps?.places) {
              clearInterval(checkInterval)
              resolve()
            }
          }, 100)
        }
      })

      const mapServices = window.google
        ?.maps as unknown as MapAddress.MapServices
      const newMapApi = GoogleMapApi({
        apiKey,
        mapServices,
        language: getLanguageCode(language, country),
      })
      setMapApi(newMapApi)
    } catch (error) {
      console.error('Failed to initialize Google Maps API:', error)
    } finally {
      setIsLoading(false)
    }
  }, [apiKey, country, language, mapApi])

  const getLocalisationParams = () => ({
    country,
    language: getLanguageCode(language, country),
  })

  const reverseGeocode = useCallback(
    async (
      lat: number,
      lng: number
    ): Promise<MapAddress.TransformedAddress[] | null> => {
      if (!mapApi) {
        await initializeMapApi()
      }
      return mapApi?.reverseGeoCode({ latitude: lat, longitude: lng }) ?? null
    },
    [mapApi, initializeMapApi]
  )

  return <>{children({ reverseGeocode, isLoading })}</>
}

export default ReverseGeocodeService
